import gen from "@/functions/general.js"

export function normalizeString(str) {
  return str.trim().normalize("NFD").replace(/\p{Diacritic}/gu, "").toUpperCase()
}

export function createExistingMedicList(commonConcepts) {
  const existingMedics = commonConcepts
    .filter(concept => concept.definingSapExpression?.aggressorRoot === "MedicSubstance" || concept.definingSapExpression?.aggressorRoot === "MedicContrastMedia")
    .filter(concept => concept.currentEntryStatus?.isSapActive === true)
    .map(concept => {
      const definingSapExpression = gen.deepCopy(concept.definingSapExpression)
      const closestExpression =concept.currentEntryStatus.closestExpression
      const priorDoseTiming = concept.currentEntryStatus.priorDoseTiming

      definingSapExpression.originalText = closestExpression.originalText
      definingSapExpression.multiDosageText = closestExpression.multiDosageText
      definingSapExpression.matchedText = closestExpression.matchedText
      definingSapExpression.unMatchedText = closestExpression.unMatchedText
      definingSapExpression.text = closestExpression.matchedText
      definingSapExpression.posoText = priorDoseTiming.originalPosologie //TODO: activeDoseTimingForDate.originalDuration
      return definingSapExpression
    })
  return existingMedics
}

export function createSubstanceReactionList(allExpressions) {
  const substanceReactions = allExpressions
    .filter(expr => expr.aggressorRootTerm === "SubstanceReaction")
    .filter(expr => expr.aggressorContentRoot === "MedicSubstance" || expr.aggressorContentRoot === "MedicContrastMedia")
    .flatMap(expr => [
      expr.sctContentFamily1,
      expr.sctContentFamily2,
      expr.sctContentFamily3,
      expr.sctContentFamily4,
      expr.sctContentFamily5,
      expr.sctContentFamily6
    ])
    .filter(substance => substance !== "")
  const uniqueSubstanceReactions = [...new Set(substanceReactions)];
  return uniqueSubstanceReactions
}

export function createDosageList(dosageLuReference) {
  const allDosages = {}
  dosageLuReference.forEach(dosage => addDosage(allDosages, dosage))

  for (const substanceKey in allDosages) {
    const routes = allDosages[substanceKey].routeInstructions
    for (const route in routes) {
      routes[route].sort(compareDosages)
    }
    allDosages[substanceKey].routeInstructions = sortRouteInstructions(routes)
  }

  return allDosages
}

export function createSubstanceList(medicReference, dosages, existingMedics, substanceReactionList) {
  let allSubstances = []
  medicReference.forEach(medic => {
    const medicTerm = medic.sctSubstanceTerm.trim()
    if (!medicTerm) return;
    let newMedic = creatMedicSearch(normalizeString(medicTerm), medicTerm, medic.sctSubstanceKey)
    newMedic.substanceTerm = medicTerm
    newMedic.sctContentFamilies = [medic.sctContentFamily]
    allSubstances.push(newMedic)
  })
  allSubstances.sort((a, b) => a.searchTerm.localeCompare(b.searchTerm))
  addMedicRefToSearchResults(allSubstances, dosages)
  addExistingMedicationToList(allSubstances, existingMedics)
  addAllergiesToSearchResults(allSubstances, substanceReactionList)    
  return allSubstances
}

export function createMultiSubstanceList(allLuMedics, allSubstances, dosages, existingMedics, substanceReactionList) {
  // allSubstances -> Create a map for quick lookup of substances
  const substanceMap = new Map()
  allSubstances.forEach(substance => {
    substanceMap.set(substance.sctSubstanceKey, substance)
  })
  // allLuMedics -> Get unique substanceKeys
  let multiSubstances = []
  let uniqueKeys = new Set()
  for (const medic of allLuMedics) {
    if (!uniqueKeys.has(medic.sctSubstanceKey)) {
      uniqueKeys.add(medic.sctSubstanceKey)
      multiSubstances.push(gen.deepCopy(medic))
    }
  }
  //Create filtered array
  multiSubstances = multiSubstances.filter(multiSubst => {
    multiSubst.substances = multiSubst.sctSubstanceKey.split("-").filter(substance => substance !== "")
    if (multiSubst.substances.length <= 1) return false;

    const substanceTerms = []
    let hasMissingSubstance = false
    for (const substanceKey of multiSubst.substances) {
      const substTermObj = substanceMap.get(substanceKey + '-----')
      const term = substTermObj?.originalText
      if (!term) { 
        hasMissingSubstance = true
      } else {
        multiSubst.sctContentFamilies.push(substTermObj.sctContentFamilies[0])
        substanceTerms.push(term)
      }
    }
    if (hasMissingSubstance) return false;
    substanceTerms.sort((a, b) => a.localeCompare(b))
    const combinedSubstanceTerm = substanceTerms.join(' + ')
    multiSubst.originalText = combinedSubstanceTerm
    multiSubst.matchedText = combinedSubstanceTerm
    multiSubst.substanceTerm = combinedSubstanceTerm
    multiSubst.searchTerm = ""
    return true
  })
  addMedicRefToSearchResults(multiSubstances, dosages)
  addExistingMedicationToList(multiSubstances, existingMedics)
  addAllergiesToSearchResults(multiSubstances, substanceReactionList)  
  return multiSubstances
}

export function createMedicLuList(medicLuReference, dosages, existingMedics, substanceReactionList) {
  let allLuMedics = []
  medicLuReference.forEach(medic => {
    const medicTerm = medic.commercialNameRoot.trim()
    if (!medicTerm) return
    let newMedic = creatMedicSearch(normalizeString(medicTerm), medicTerm, medic.sctSubstanceKey)
    newMedic.originalText = medicTerm
    newMedic.matchedText = medicTerm
    newMedic.aggressorRoot = medic.aggressorRoot
    newMedic.substanceTerm = [medic.vidalSubstance1, medic.vidalSubstance2, medic.vidalSubstance3, medic.vidalSubstance4, medic.vidalSubstance5, medic.vidalSubstance6]
      .filter(term => term !== '')
      .map(term => term.charAt(0).toUpperCase() + term.slice(1).toLowerCase())
      .sort((a, b) => a.localeCompare(b))
      .join(' + ')
      newMedic.sctContentFamilies = [medic.sctContentFamily1, medic.sctContentFamily2, medic.sctContentFamily3, medic.sctContentFamily4, medic.sctContentFamily5, medic.sctContentFamily6]
      .filter(term => term !== '')    

    allLuMedics.push(newMedic)
  })
  //No Duplicates, and prioritize aggressorRoot === 'Medic'
  const medicMap = new Map()
  allLuMedics.forEach(medic => {
    const nameRoot = medic.originalText
    const existing = medicMap.get(nameRoot)
    if (!existing || (existing.aggressorRoot === "None" && medic.aggressorRoot !== "None")) {
        medicMap.set(nameRoot, medic)
    }
  })
  allLuMedics =  Array.from(medicMap.values())

  //No duplicates
  // let uniqueTerms = new Set()
  // allLuMedics = allLuMedics
  //   .filter(medic => {
  //     if (uniqueTerms.has(medic.searchTerm)) return false;
  //     uniqueTerms.add(medic.searchTerm)
  //     return true
  //   })
  allLuMedics.sort((a, b) =>a.searchTerm.localeCompare(b.searchTerm))
  addMedicRefToSearchResults(allLuMedics, dosages)
  addExistingMedicationToList(allLuMedics, existingMedics)
  addAllergiesToSearchResults(allLuMedics, substanceReactionList)
  return allLuMedics
}

//SORT FUNCTION
export function sortMedicRefList(medicList) {
  medicList.sort((a, b) => {
    if (a.hasExistingsMedics && !b.hasExistingsMedics) return -1
    if (!a.hasExistingsMedics && b.hasExistingsMedics) return 1
    return 0;
  })
}

//HELPER FUNCTIONS
function creatMedicSearch(searchTerm, originalText, sctSubstanceKey) {
  return {
    "searchTerm": searchTerm,     //Substnace (DCI) or commercialName
    "originalText": originalText, //CommercialName (used for creation)
    "matchedText": originalText, //CommercialName (used for creation)
    "substanceTerm" : "",         //Substance(s) (DCI)
    "sctSubstanceKey": sctSubstanceKey,
    "sctContentFamilies" : [],

    "hasAllergy": false,
    "hasExistingsMedics": false,
  }
}

function addAllergiesToSearchResults(medicList, substanceReactionList) {
  medicList.forEach(listMedic => {
    const hasAllergy = listMedic.sctContentFamilies.some(family => substanceReactionList.includes(family))
    listMedic.hasAllergy = hasAllergy
  })
}

function addMedicRefToSearchResults(medicList, dosages) {
  medicList.forEach(medic => {
    if (!medic.sctSubstanceKey) return;
    const medicReference = dosages[medic.sctSubstanceKey]
    if (!medicReference) return;
  })
}

function addExistingMedicationToList(medicList, existingMedics) {
  medicList.forEach(listMedic => {
    if (!listMedic.sctSubstanceKey) return;
    const existingMedic = existingMedics.find(m => m.sctSubstanceKey === listMedic.sctSubstanceKey)
    if (!existingMedic) return;
    listMedic.hasExistingsMedics = true
  })
}

const addDosage = (allDosages, dosage) => {
  const { sctSubstanceKey, sctAdministrationRoute } = dosage
  if (!sctSubstanceKey) return
  if (!allDosages[sctSubstanceKey]) {
    allDosages[sctSubstanceKey] = { routeInstructions: {} }
  }
  if (!allDosages[sctSubstanceKey].routeInstructions[sctAdministrationRoute]) {
    allDosages[sctSubstanceKey].routeInstructions[sctAdministrationRoute] = []
  }
  allDosages[sctSubstanceKey].routeInstructions[sctAdministrationRoute].push({ term: dosage.originalPoso })
}

const isOralRoute = (route) =>
  /oral|per os|^po$/i.test(route)

const compareRoutes = ([a], [b]) => {
  const aOral = isOralRoute(a)
  const bOral = isOralRoute(b)
  if (aOral && !bOral) return -1
  if (!aOral && bOral) return 1
  return 0
}

const sortRouteInstructions = (routes) =>
  Object.fromEntries(Object.entries(routes).sort(compareRoutes))

const compareDosages = (a, b) =>
  a.term.localeCompare(b.term, undefined, { numeric: true, sensitivity: 'base' })


